import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Lightbulb, AlertCircle, Clock, RefreshCw, Zap, Brain } from "lucide-react";
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from "recharts";

const catMeta = {
  silly_mistake: { label: "Silly mistakes", icon: AlertCircle, color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-700" },
  concept_gap: { label: "Concept gaps", icon: Brain, color: "#e11d48", bg: "bg-rose-50", text: "text-rose-700" },
  time_pressure: { label: "Time pressure", icon: Clock, color: "#ea580c", bg: "bg-orange-50", text: "text-orange-700" },
  second_guessing: { label: "Second-guessing", icon: RefreshCw, color: "#7c3aed", bg: "bg-violet-50", text: "text-violet-700" },
  guessing: { label: "Guessing", icon: Zap, color: "#78716c", bg: "bg-stone-100", text: "text-stone-700" },
};

export default function ErrorCategories({ attemptId, exam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (trigger = false) => {
    setLoading(true);
    try {
      const r = await api.post(`/attempts/${attemptId}/categorize-errors`);
      setData(r.data.categories);
      if (trigger) toast.success("AI mistake analysis ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Categorization failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Try cached
    (async () => {
      try {
        const r = await api.get(`/attempts/${attemptId}`);
        if (r.data.attempt?.error_categorization) setData(r.data.attempt.error_categorization);
      } catch (err) {
        console.error("cached categorization load failed:", err);
      }
    })();
  }, [attemptId]);

  if (!data) {
    return (
      <Card className="p-8 rounded-md border-stone-200 shadow-none text-center">
        <Sparkles className="h-8 w-8 mx-auto text-orange-600 mb-3" />
        <h3 className="font-heading text-xl font-semibold mb-2">AI Error Analysis</h3>
        <p className="text-sm text-stone-600 mb-4 max-w-md mx-auto">
          Let Gemini classify each of your mistakes into: silly mistakes, concept gaps, time pressure, second-guessing, or guessing — with a coach tip per error.
        </p>
        <Button
          onClick={() => load(true)}
          disabled={loading}
          className="bg-orange-600 hover:bg-orange-700 text-white h-11"
          data-testid="categorize-errors-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Analyze my mistakes
        </Button>
      </Card>
    );
  }

  const summary = data.summary || {};
  const totalErrors = Object.values(summary).reduce((s, v) => s + v, 0);

  if (totalErrors === 0) {
    return (
      <Card className="p-10 rounded-md border-stone-200 shadow-none text-center">
        <div className="text-4xl">🏆</div>
        <h3 className="font-heading text-xl font-semibold mt-3">No wrong answers to analyse</h3>
        <p className="text-sm text-stone-500 mt-1">Every attempted question was correct. Keep it up.</p>
      </Card>
    );
  }

  const q_map = Object.fromEntries((exam?.structure?.questions || []).map((q) => [q.q_id, q]));

  const radialData = Object.entries(summary)
    .filter(([_, v]) => v > 0)
    .map(([k, v]) => ({ name: catMeta[k]?.label || k, value: v, fill: catMeta[k]?.color || "#78716c" }));

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-md border-stone-200 shadow-none">
          <h3 className="font-heading text-xl font-semibold mb-4">Mistake breakdown</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <RadialBarChart innerRadius="30%" outerRadius="100%" data={radialData} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, totalErrors]} tick={false} />
                <RadialBar dataKey="value" background={{ fill: "#f5f5f4" }} cornerRadius={4} />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-6 rounded-md border-stone-200 shadow-none">
          <h3 className="font-heading text-xl font-semibold mb-4">Summary</h3>
          <div className="space-y-2">
            {Object.entries(summary).map(([k, v]) => {
              const meta = catMeta[k] || { label: k, icon: AlertCircle, color: "#78716c" };
              const Icon = meta.icon;
              const pct = totalErrors ? Math.round((v / totalErrors) * 100) : 0;
              return (
                <div key={k} className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-md ${meta.bg} flex items-center justify-center`}>
                    <Icon className={`h-4 w-4 ${meta.text}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{meta.label}</span>
                      <span className="font-mono text-stone-500 tabular-nums">{v} · {pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="p-6 rounded-md border-stone-200 shadow-none">
        <h3 className="font-heading text-xl font-semibold mb-4">Per-mistake coach tips</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2" data-testid="error-items">
          {(data.items || []).map((it) => {
            const meta = catMeta[it.category] || { label: it.category, icon: AlertCircle, color: "#78716c", bg: "bg-stone-100", text: "text-stone-700" };
            const Icon = meta.icon;
            const q = q_map[it.q_id];
            return (
              <div key={`${it.q_id}-${it.category}`} className="border border-stone-200 rounded-md p-4">
                <div className="flex items-start gap-3">
                  <div className={`h-9 w-9 rounded-md ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${meta.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-stone-500">Q{q?.number || "?"}</span>
                      <Badge variant="secondary" className={`${meta.bg} ${meta.text} border-none font-mono text-[10px] uppercase tracking-[0.15em]`}>
                        {meta.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-800 leading-snug line-clamp-2">{q?.text}</p>
                    <div className="mt-2 flex items-start gap-2 text-sm text-stone-700">
                      <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>{it.tip}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
